import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  InputGroup,
  InputLeftElement,
  Textarea
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, EditIcon, DeleteIcon, ChevronDownIcon, AttachmentIcon } from '@chakra-ui/icons';
import apiService from '../../services/ApiService';

const ContentManagement = () => {
  const [content, setContent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedContent, setSelectedContent] = useState(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  // ฟังก์ชันสำหรับปิด Modal และ Reset State
  const handleCloseModal = () => {
    onClose();
    setSelectedContent(null);
    setFormData({
      category_id: '',
      title: '',
      description: '',
      content_type: 'เอกสาร', // ตั้งค่าเป็นเอกสารเสมอ
      year_type: '',
      link: '',
      is_active: true
    });
    setSelectedFile(null);
    setYearTypeList([]);
    setCustomYearType('');
  };

  const handleCloseEditModal = () => {
    onEditClose();
    setSelectedContent(null);
    setFormData({
      category_id: '',
      title: '',
      description: '',
      content_type: 'เอกสาร', // ตั้งค่าเป็นเอกสารเสมอ
      year_type: '',
      link: '',
      is_active: true
    });
    setSelectedFile(null);
    setYearTypeList([]);
    setCustomYearType('');
  };
  
  const [formData, setFormData] = useState({
    category_id: '',
    title: '',
    description: '',
    content_type: 'เอกสาร', // ตั้งค่าเป็นเอกสารเสมอ
    year_type: '',
    link: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [yearTypeList, setYearTypeList] = useState([]);
  const [customYearType, setCustomYearType] = useState('');
  
  const toast = useToast();

  useEffect(() => {
    fetchContent();
    fetchCategories();
  }, [currentPage, searchTerm, categoryFilter]);

  const fetchContent = async () => {
    try {
      setIsLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(categoryFilter && { category_id: categoryFilter })
      });

      const response = await apiService({
        method: 'GET',
        url: `/content?${params}`
      });

      setContent(response.data.content);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error fetching content:', error);
      setError('ไม่สามารถโหลดข้อมูลเนื้อหาได้');
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดข้อมูลเนื้อหาได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService({
        method: 'GET',
        url: '/categories?limit=100'
      });
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchYearTypeList = async (categoryId, currentYearType = null) => {
    if (!categoryId) {
      setYearTypeList([]);
      return;
    }

    try {
      const response = await apiService({
        method: 'GET',
        url: `/content?category_id=${categoryId}&limit=1000`
      });
      
      if (response.data && response.data.content) {
        // ดึง year_type ที่ไม่ซ้ำและไม่เป็น null/empty
        const yearTypes = [...new Set(
          response.data.content
            .map(item => item.year_type)
            .filter(yearType => yearType && yearType.trim() !== '')
        )];
        
        // เรียงจากมากไปหาน้อย (ปีใหม่ก่อน)
        const sortedYearTypes = yearTypes.sort((a, b) => {
          // ถ้าเป็นตัวเลข 4 หลัก (ปี พ.ศ.)
          const aNum = parseInt(a);
          const bNum = parseInt(b);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return bNum - aNum; // เรียงจากมากไปหาน้อย
          }
          // ถ้าไม่ใช่ตัวเลข ให้เรียงตามตัวอักษร
          return b.localeCompare(a);
        });
        
        setYearTypeList(sortedYearTypes);
        
        // ตั้งค่า default
        if (currentYearType && currentYearType.trim() !== '') {
          // ถ้ามี year_type เดิม ให้ใช้ค่านั้น
          setFormData(prev => ({
            ...prev,
            year_type: currentYearType
          }));
        } else if (sortedYearTypes.length > 0) {
          // ถ้าไม่มี year_type เดิม ให้ใช้ค่าแรก
          setFormData(prev => ({
            ...prev,
            year_type: sortedYearTypes[0]
          }));
        } else {
          // ถ้าไม่มี year_type list ให้ใช้ค่าว่าง
          setFormData(prev => ({
            ...prev,
            year_type: ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching year types:', error);
      setYearTypeList([]);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (e) => {
    setCategoryFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateContent = () => {
    setFormData({
      category_id: '',
      title: '',
      description: '',
      content_type: 'เอกสาร',
      year_type: '',
      link: ''
    });
    setFormErrors({});
    setSelectedFile(null);
    onOpen();
  };

  const handleEditContent = (contentItem) => {
    setSelectedContent(contentItem);
    setFormData({
      category_id: contentItem.category_id,
      title: contentItem.title,
      description: contentItem.description || '',
      content_type: 'เอกสาร', // ตั้งค่าเป็นเอกสารเสมอ
      year_type: contentItem.year_type || '',
      link: contentItem.link || ''
    });
    setFormErrors({});
    setSelectedFile(null);
    
    // ดึง year_type list สำหรับหมวดหมู่นี้ และส่ง year_type เดิม
    fetchYearTypeList(contentItem.category_id, contentItem.year_type);
    
    onEditOpen();
  };

  const handleDeleteContent = (contentItem) => {
    setSelectedContent(contentItem);
    onDeleteOpen();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // ถ้าเปลี่ยนหมวดหมู่ ให้ดึง year_type list (ไม่ส่ง year_type เดิม)
    if (name === 'category_id') {
      fetchYearTypeList(value);
    }
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.category_id) {
      errors.category_id = 'กรุณาเลือกหมวดหมู่';
    }
    
    if (!formData.title.trim()) {
      errors.title = 'กรุณากรอกหัวข้อ';
    }
    
    if (formData.content_type === 'ลิงก์' && !formData.link.trim()) {
      errors.link = 'กรุณากรอกลิงก์';
    }
    
    if (formData.content_type === 'เอกสาร' && !selectedFile && !selectedContent) {
      errors.file = 'กรุณาเลือกไฟล์';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      console.log('Form Data:', formData);
      console.log('Selected File:', selectedFile);
      
      const formDataToSend = new FormData();
      formDataToSend.append('category_id', formData.category_id);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('content_type', formData.content_type);
      formDataToSend.append('year_type', formData.year_type);
      formDataToSend.append('link', formData.link);
      // ไม่ใช้ sort_order แล้ว
      
      if (selectedFile) {
        formDataToSend.append('file', selectedFile);
      }
      
      console.log('FormData entries:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}: ${value}`);
      }

      if (selectedContent) {
        // Update content
        await apiService({
          method: 'PUT',
          url: `/content/${selectedContent.id}`,
          data: formDataToSend
        });
        
        toast({
          title: 'อัปเดตเนื้อหาสำเร็จ',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onEditClose();
      } else {
        // Create content
        await apiService({
          method: 'POST',
          url: '/content',
          data: formDataToSend
        });
        
        toast({
          title: 'สร้างเนื้อหาสำเร็จ',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
      }
      
      fetchContent();
    } catch (error) {
      console.error('Error saving content:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถบันทึกข้อมูลได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService({
        method: 'DELETE',
        url: `/content/${selectedContent.id}`
      });
      
      toast({
        title: 'ลบเนื้อหาสำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onDeleteClose();
      fetchContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถลบเนื้อหาได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const toggleContentStatus = async (contentItem) => {
    try {
      await apiService({
        method: 'PUT',
        url: `/content/${contentItem.id}`,
        data: { is_active: !contentItem.is_active }
      });
      
      toast({
        title: 'อัปเดตสถานะสำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      fetchContent();
    } catch (error) {
      console.error('Error updating content status:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัปเดตสถานะได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getContentTypeText = (type) => {
    switch (type) {
      case 'document': return 'เอกสาร';
      case 'link': return 'ลิงก์';
      case 'text': return 'ข้อความ';
      default: return type;
    }
  };

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'document': return 'blue';
      case 'link': return 'green';
      case 'text': return 'gray';
      default: return 'gray';
    }
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <VStack>
          <Spinner size="xl" color="blue.500" />
          <Text>กำลังโหลดข้อมูล...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">จัดการเนื้อหา</Text>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreateContent}>
          เพิ่มเนื้อหา
        </Button>
      </Flex>

      {/* Filters */}
      <Box bg="white" p={4} rounded="lg" shadow="sm" mb={6}>
        <HStack spacing={4}>
          <InputGroup maxW="300px">
            <InputLeftElement>
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="ค้นหาเนื้อหา..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </InputGroup>
          
          <Select
            placeholder="เลือกหมวดหมู่"
            value={categoryFilter}
            onChange={handleCategoryFilter}
            maxW="200px"
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.title}
              </option>
            ))}
          </Select>
        </HStack>
      </Box>

      {/* Content Table */}
      <Box bg="white" rounded="lg" shadow="sm" overflow="hidden">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>หัวข้อ</Th>
              <Th>หมวดหมู่</Th>
              <Th>ประเภท</Th>
              <Th>คำอธิบาย</Th>
              <Th>สถานะ</Th>
              <Th>จัดการ</Th>
            </Tr>
          </Thead>
          <Tbody>
            {content.map((contentItem) => (
              <Tr key={contentItem.id}>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium" noOfLines={2}>
                      {contentItem.title}
                    </Text>
                    {contentItem.file_url && (
                      <HStack>
                        <AttachmentIcon boxSize={3} color="blue.500" />
                        <Text fontSize="xs" color="blue.500">
                          มีไฟล์แนบ
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </Td>
                <Td>
                  <Text fontSize="sm" color="gray.600">
                    {contentItem.category_title || 'ไม่มีหมวดหมู่'}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={getContentTypeColor(contentItem.content_type)}>
                    {getContentTypeText(contentItem.content_type)}
                  </Badge>
                </Td>
                <Td>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {contentItem.description || '-'}
                  </Text>
                </Td>
                <Td>
                  <Badge 
                    colorScheme={contentItem.is_active ? 'green' : 'red'}
                    cursor="pointer"
                    onClick={() => toggleContentStatus(contentItem)}
                  >
                    {contentItem.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                  </Badge>
                </Td>
                <Td>
                  <Menu>
                    <MenuButton as={IconButton} icon={<ChevronDownIcon />} variant="ghost" size="sm" />
                    <MenuList>
                      <MenuItem icon={<EditIcon />} onClick={() => handleEditContent(contentItem)}>
                        แก้ไข
                      </MenuItem>
                      <MenuItem icon={<DeleteIcon />} onClick={() => handleDeleteContent(contentItem)} color="red.500">
                        ลบ
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="center" mt={6}>
          <Button
            isDisabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ก่อนหน้า
          </Button>
          <Text>
            หน้า {currentPage} จาก {totalPages}
          </Text>
          <Button
            isDisabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            ถัดไป
          </Button>
        </HStack>
      )}

      {/* Create/Edit Content Modal */}
      <Modal 
        isOpen={isOpen || isEditOpen} 
        onClose={selectedContent ? handleCloseEditModal : handleCloseModal} 
        size="lg"
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedContent ? 'แก้ไขเนื้อหา' : 'เพิ่มเนื้อหาใหม่'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isInvalid={formErrors.category_id}>
                  <FormLabel>หมวดหมู่</FormLabel>
                  <Select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleFormChange}
                    placeholder="เลือกหมวดหมู่"
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.title}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{formErrors.category_id}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={formErrors.title}>
                  <FormLabel>หัวข้อ</FormLabel>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="กรอกหัวข้อ"
                  />
                  <FormErrorMessage>{formErrors.title}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>คำอธิบาย</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="กรอกคำอธิบาย"
                    rows={3}
                  />
                </FormControl>

                {/* ประเภทเนื้อหา: เอกสาร (ไม่แสดง UI) */}

                {/* ลิงก์: ไม่ใช้แล้ว (ประเภทเนื้อหาเป็นเอกสารเสมอ) */}

                {/* ปีงบประมาณ/ประเภทเอกสาร */}
                <FormControl isInvalid={formErrors.year_type}>
                  <FormLabel>ปีงบประมาณ/ประเภทเอกสาร</FormLabel>
                  {yearTypeList.length > 0 ? (
                    <VStack spacing={2} align="stretch">
                      <Select
                        name="year_type"
                        value={formData.year_type}
                        onChange={handleFormChange}
                        placeholder="เลือกปีงบประมาณ/ประเภทเอกสาร"
                      >
                        {yearTypeList.map((yearType, index) => (
                          <option key={index} value={yearType}>
                            {yearType}
                          </option>
                        ))}
                      </Select>
                      <Text fontSize="sm" color="gray.600">
                        หรือกรอกใหม่:
                      </Text>
                      <Input
                        name="custom_year_type"
                        value={customYearType}
                        onChange={(e) => setCustomYearType(e.target.value)}
                        placeholder="กรอกปีงบประมาณ/ประเภทเอกสารใหม่"
                        onBlur={() => {
                          if (customYearType.trim()) {
                            setFormData(prev => ({
                              ...prev,
                              year_type: customYearType.trim()
                            }));
                          }
                        }}
                      />
                    </VStack>
                  ) : (
                    <Input
                      name="year_type"
                      value={formData.year_type}
                      onChange={handleFormChange}
                      placeholder="กรอกปีงบประมาณ/ประเภทเอกสาร เช่น 2568, 2567, 2566"
                    />
                  )}
                  <FormErrorMessage>{formErrors.year_type}</FormErrorMessage>
                </FormControl>
                
                {/* ไฟล์เอกสาร */}
                <FormControl isInvalid={formErrors.file}>
                  <FormLabel>ไฟล์เอกสาร</FormLabel>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    onChange={handleFileChange}
                  />
                  <FormErrorMessage>{formErrors.file}</FormErrorMessage>
                </FormControl>


                <HStack w="100%" justify="flex-end">
                  <Button variant="ghost" onClick={selectedContent ? handleCloseEditModal : handleCloseModal}>
                    ยกเลิก
                  </Button>
                  <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
                    {selectedContent ? 'อัปเดต' : 'สร้าง'}
                  </Button>
                </HStack>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={isDeleteOpen} 
        onClose={onDeleteClose}
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ยืนยันการลบ</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text>
              คุณแน่ใจหรือไม่ที่จะลบเนื้อหา <strong>{selectedContent?.title}</strong>?
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </Text>
            <HStack mt={4} justify="flex-end">
              <Button variant="ghost" onClick={onDeleteClose}>
                ยกเลิก
              </Button>
              <Button colorScheme="red" onClick={handleDelete}>
                ลบ
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ContentManagement;

